import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Tabs, Spin, Button, Tooltip } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { DEFAULT_TEMPLATES, FREE_FORM_EDITOR_ID } from '../../const';
import QuestionTab from './QuestionTab';
import IndividualProblem from './IndividualProblem';
import FreeFormEditor from '../../components/Editor/FreeFormEditor';
import {
  fetchLatestEditorEvent,
  addCodingProblem,
  updateCodingProblemVisibility,
  deleteCodingProblem,
} from '../../store/actions/InterviewActions';

const ProblemTabs = ({
  problemDetails: propProblemDetails,
  isDeveloper,
  wsRef,
  getCodingProblems,
  setProblemDetails,
  interview_id,
  onCodeInEditorChange,
  switchLanguage,
  submitCode,
  submitFreeFormCode,
  freeFormOutput,
  editorExpand,
  toggleExpand,
  onActiveTabChange,
  onFreeFormOutputChange,
}) => {
  const dispatch = useDispatch();
  const [activeKey, setActiveKey] = useState(() => (isDeveloper ? '3' : ''));
  const [items, setItems] = useState([]);
  const { loading } = useSelector(state => state.problem);
  const { currentLanguageforProblems } = useSelector(state => state.events);
  const problemDetails = propProblemDetails;

  const tabStyles = `
    .ant-tabs-content {
      height: 100%;
    }
    .ant-tabs-tabpane {
      height: 100%;
    }
    .main-problem-tabs > .ant-tabs-nav {
      margin-bottom: 0 !important;
    }
    .ant-splitter .ant-splitter-panel {
    }
  `;

  const handleSubmitCode = useCallback(() => {
    const freeFormEditor = currentLanguageforProblems.find(
      item => item.problem_id === FREE_FORM_EDITOR_ID,
    );
    const code = freeFormEditor?.code || '# write code here';
    const language = freeFormEditor?.language || 'python3';

    submitFreeFormCode(code, language);
  }, [currentLanguageforProblems]);

  const initialItems = useMemo(() => {
    const items = [];
    if (isDeveloper) {
      items.push({
        label: 'Add Questions',
        children: <QuestionTab />,
        key: '3',
        closable: false,
        isProblem: false,
      });
    }
    items.push({
      label: 'CodeEditor',
      children: (
        <FreeFormEditor
          output={freeFormOutput}
          selectedLanguage={
            currentLanguageforProblems.find(item => item.problem_id === FREE_FORM_EDITOR_ID)
              ?.language || 'python3'
          }
          code={
            currentLanguageforProblems.find(item => item.problem_id === FREE_FORM_EDITOR_ID)
              ?.code || '# write code here'
          }
          updateCode={newCode =>
            onCodeInEditorChange(
              newCode,
              currentLanguageforProblems.find(item => item.problem_id === FREE_FORM_EDITOR_ID)
                ?.language || 'python3',
              FREE_FORM_EDITOR_ID,
            )
          }
          switchLanguage={newLanguage => switchLanguage(newLanguage, FREE_FORM_EDITOR_ID)}
          templates={DEFAULT_TEMPLATES}
          submitCode={handleSubmitCode}
          disabled={false}
          activeTemplate={null}
          onOutputChange={onFreeFormOutputChange}
        />
      ),
      key: FREE_FORM_EDITOR_ID,
      closable: false,
      isProblem: false,
    });
    return items;
  }, [
    isDeveloper,
    currentLanguageforProblems,
    switchLanguage,
    onCodeInEditorChange,
    interview_id,
    handleSubmitCode,
    freeFormOutput,
    onFreeFormOutputChange,
  ]);

  const addCodingProblems = async (problem, visibility) => {
    try {
      const existingProblemsCount = items.filter(item => item.isProblem).length;
      const newOrder = existingProblemsCount + 1;

      await dispatch(
        addCodingProblem(interview_id, problem.id, visibility, newOrder, problem.points),
      );

      await dispatch(getCodingProblems(interview_id));

      const updatedProblems = await dispatch((dispatch, getState) => {
        return Promise.resolve(getState().problem.problemDetails);
      });

      if (Array.isArray(updatedProblems)) {
        const matchedProblem = updatedProblems.find(item => item.problem_details.id === problem.id);
        return matchedProblem || null;
      }

      return null;
    } catch (error) {
      console.error('Error in addCodingProblems:', error);
      return null;
    }
  };

  useEffect(() => {
    getCodingProblems(interview_id);
  }, [getCodingProblems, interview_id]);

  useEffect(() => {
    if (Array.isArray(problemDetails)) {
      if (problemDetails.length > 0) {
        const filteredProblems = isDeveloper
          ? problemDetails
          : problemDetails.filter(problem => problem.visibility_status === true);

        const problemItems = filteredProblems.map((problem, index) => {
          const problemId = problem.problem_details.id;
          return {
            label: `Problem ${index + 1}`,
            children: (
              <IndividualProblem
                question={problem}
                onCodeInEditorChange={onCodeInEditorChange}
                switchLanguage={switchLanguage}
                wsRef={wsRef}
                submitCode={submitCode}
                editorExpand={editorExpand}
                toggleExpand={toggleExpand}
                problemId={problemId}
              />
            ),
            key: `${problemId}`,
            closable: isDeveloper,
            isProblem: true,
          };
        });

        const newItems = [...problemItems, ...initialItems];
        setItems(newItems);

        const currentActiveExists = newItems.some(item => item.key === activeKey);

        if (!currentActiveExists) {
          setActiveKey(newItems[0]?.key || '');
          wsRef.current && activeTabChange('', newItems[0]?.key || '');
        }
      } else {
        setItems(initialItems);
        const isFound = initialItems.find(item => item.key === activeKey);
        if (isFound) return;
        setActiveKey(initialItems[0]?.key || '');
        wsRef.current && activeTabChange('', initialItems[0]?.key || '');
      }
    }
  }, [problemDetails, activeKey, initialItems, isDeveloper]);

  useEffect(() => {
    if (onActiveTabChange) {
      const isProblemTab = !isNaN(parseInt(activeKey, 10));
      onActiveTabChange(activeKey, isProblemTab);
    }
  }, [activeKey, onActiveTabChange]);

  const activeTabChange = (from, to) => {
    wsRef.current.send(
      JSON.stringify({
        type: 'active_tab_change',
        data: {
          from: from,
          to: to,
        },
      }),
    );
  };
  const onChange = newActiveKey => {
    const from = activeKey;
    setActiveKey(newActiveKey);
    activeTabChange(from, newActiveKey);
    const selectedItem = currentLanguageforProblems.find(item => item.problem_id === newActiveKey);

    dispatch(
      fetchLatestEditorEvent(
        interview_id,
        selectedItem?.language ?? 'python3',
        newActiveKey,
        selectedItem?.code,
      ),
    );
  };

  const addQuestionTab = async (question, visibility) => {
    const addedProblem = await addCodingProblems(question, !visibility);

    if (addedProblem) {
      setActiveKey(`${addedProblem.problem_details.id}`);
      if (addedProblem.visibility_status) {
        activeTabChange('', `${addedProblem.problem_details.id}`);
      }
    }
  };

  const toggleVisibility = async (problemId, currentVisibility) => {
    try {
      await dispatch(updateCodingProblemVisibility(interview_id, problemId, !currentVisibility));
      await dispatch(getCodingProblems(interview_id));
    } catch (error) {
      console.error('Error toggling visibility:', error);
    }
  };

  const remove = async targetKey => {
    try {
      await dispatch(deleteCodingProblem(interview_id, targetKey));
      await dispatch(getCodingProblems(interview_id));
    } catch (error) {
      console.error('Error removing problem:', error);
      try {
        await dispatch(getCodingProblems(interview_id));
      } catch (refreshError) {
        console.error('Error refreshing problems:', refreshError);
      }
    }
  };

  const onEdit = (targetKey, action) => {
    if (action === 'add') {
      addQuestionTab();
    } else {
      remove(targetKey);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          flexDirection: 'column',
        }}
      >
        <Spin size="large" />
        <div style={{ marginTop: '16px', fontSize: '16px', color: '#666' }}>
          Loading problem details...
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{tabStyles}</style>
      <Tabs
        className="main-problem-tabs"
        type="editable-card"
        onChange={onChange}
        activeKey={activeKey}
        onEdit={onEdit}
        hideAdd
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        {(() => {
          let problemCount = 1;
          return items.map(item => {
            const problem = problemDetails.find(p => p.problem_details.id.toString() === item.key);
            const isVisible = problem?.visibility_status;

            return (
              <Tabs.TabPane
                className="green-yellow"
                key={item.key}
                style={{ background: '#FFFFFF', height: '100%' }}
                tab={
                  item.isProblem ? (
                    <span>
                      {isDeveloper && (
                        <Tooltip
                          placement="top"
                          title={'ToggleVisibility'}
                          arrow={true}
                        >
                          <Button
                            icon={isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                            onClick={() => {
                              toggleVisibility(problem.problem_details.id, isVisible);
                            }}
                            style={{
                              marginRight: 8,
                              border: 'none',
                              background: 'inherit',
                              boxShadow: 'none',
                            }}
                          />
                        </Tooltip>
                      )}
                      {`Problem ${problemCount++}`}
                    </span>
                  ) : (
                    item.label
                  )
                }
                closable={item.closable}
              >
                {item.key === '3' ? (
                  <QuestionTab onAddQuestionTab={addQuestionTab} />
                ) : (
                  item.children
                )}
              </Tabs.TabPane>
            );
          });
        })()}
      </Tabs>
    </>
  );
};

export default ProblemTabs;
